import {
  formatChangedFieldValue,
  formatDateTime,
  formatMoney,
  measurementLabel,
  lifecycleLabel,
  productStatusLabel,
  type Translate,
} from '@/entities/moderation';
import { describePatchEntry, patchFieldEntries } from './patch-values';

/**
 * Human labels for entity / payload field names (`changed_fields`, payload
 * keys). Unknown keys fall back to a prettified key, never the raw snake
 * case.
 */
const FIELD_FALLBACKS: Record<string, string> = {
  name: 'Название',
  description: 'Описание',
  price: 'Цена',
  effective_price: 'Цена',
  id: 'ID товара',
  created_at: 'Дата создания',
  is_liked: 'Понравился',
  lifecycle_status: 'Статус жизненного цикла',
  like_count: 'Количество отметок «Нравится»',
  moderation_state: 'Статус модерации',
  rejection_reason: 'Причина отклонения',
  images: 'Фотографии',
  image_patches: 'Изображения',
  modifiers: 'Модификаторы',
  companions: 'Группы модификаторов',
  product_category_id: 'Категория',
  cook_time_seconds: 'Время приготовления',
  measurement_type: 'Единица измерения',
  weight_grams: 'Вес',
  measure_amount: 'Количество',
  status: 'Доступность',
  stock_quantity: 'Остаток',
  is_always_ready: 'Всегда в наличии',
  ikpu_code: 'ИКПУ',
  marked_code: 'Код маркировки',
  has_marked_code: 'Маркировка',
  unit_code: 'Код единицы',
  vat_percent: 'НДС, %',
  min_order_amount: 'Минимальная сумма заказа',
  restaurant_price_patches: 'Цены в филиалах',
  restaurant_products: 'Цены в филиалах',
  modifier_option_states: 'Доступность модификаторов',
  address: 'Адрес',
  working_hours: 'График работы',
  week_schedule: 'График работы',
  phone: 'Телефон',
  free_delivery_threshold: 'Сумма бесплатной доставки',
  icon: 'Иконка',
  background_image: 'Фон',
  restaurant_category_ids: 'Категории',
  min_prep_time: 'Мин. время приготовления',
  max_prep_time: 'Макс. время приготовления',
  delivery_provider: 'Доставка',
  timezone: 'Часовой пояс',
  promo_type: 'Тип акции',
  discount_type: 'Тип скидки',
  discount_value: 'Размер скидки',
  buy_quantity: 'Купить',
  get_quantity: 'Получить',
  starts_at: 'Начало',
  ends_at: 'Окончание',
  product_ids: 'Товары',
  is_active: 'Активность',
  restaurant_id: 'Филиал',
  min_select: 'Мин. выбор',
  max_select: 'Макс. выбор',
  options: 'Варианты',
  external_id: 'Внешний ID',
  clear_min_order_amount: 'Очистить минимальную сумму заказа',
  cook_time: 'Время приготовления',
  clear_availability_window: 'Сбросить часы продажи',
  clear_vat_percent: 'Сбросить ставку НДС',
  availability_window: 'Часы продажи',
  product_category_name: 'Название категории',
  terminated_reason: 'Причина завершения',
  termination_reason: 'Причина завершения',
  coordinates: 'Координаты',
  latitude: 'Широта',
  longitude: 'Долгота',
  accepting_orders_now: 'Принимает заказы сейчас',
  address_line: 'Адресная строка',
  category_ids: 'ID категорий',
  category_id: 'Категория',
  delivery_payment: 'Оплата доставки',
  menu_source: 'Источник меню',
  applied_promotion_id: 'Применённая акция',
  available_from: 'Доступно с',
  available_to: 'Доступно до',
  is_adult: 'Только 18+',
  modifiers_disabled: 'Модификаторы отключены',
  unavailable_reason: 'Причина недоступности',
  modifier_links: 'Связи с модификаторами',
  order_from: 'Приём заказов с',
  order_to: 'Приём заказов до',
};

export function fieldLabel(key: string, t: Translate): string {
  const fallback = FIELD_FALLBACKS[key];
  if (fallback !== undefined) return t(`fields.${key}`, fallback);
  const words = key.replace(/_/g, ' ').trim();
  return words === '' ? '—' : words.charAt(0).toUpperCase() + words.slice(1);
}

const MONEY_KEYS = new Set([
  'price',
  'effective_price',
  'free_delivery_threshold',
  'min_order_amount',
]);

/**
 * Display value of one entity / payload field: money as `25 000 сум`,
 * seconds as minutes, booleans as yes/no, timestamps in Tashkent wall time,
 * lifecycle and availability enums in words; anything else through the
 * generic bag formatter.
 */
export function formatFieldValue(key: string, value: unknown, t: Translate): string {
  if (value === null || value === undefined || value === '') return '—';
  const patches = patchFieldEntries(key, value);
  if (patches) {
    return patches.map((entry) => describePatchEntry(entry, t)).join('; ');
  }
  if (key === 'unavailable_reason' && typeof value === 'string') {
    return unavailableReasonLabel(value, t);
  }
  if (key === 'unit_code' && typeof value === 'string') {
    const unit = { '1': 'kg', '2': 'g', '3': 'l', '4': 'ml', '5': 'pcs', '6': 'portion' }[
      value
    ];
    return unit ? t(`fiscalUnits.${unit}`, value) : value;
  }
  if (key === 'measurement_type' && typeof value === 'string') {
    const unit = measurementLabel(value).toLowerCase();
    return t(`displayUnits.${unit}`, unit);
  }
  if (key === 'coordinates' && typeof value === 'object' && value !== null) {
    const coordinates = value as { latitude?: unknown; longitude?: unknown };
    const latitude =
      typeof coordinates.latitude === 'string' || typeof coordinates.latitude === 'number'
        ? coordinates.latitude
        : '—';
    const longitude =
      typeof coordinates.longitude === 'string' ||
      typeof coordinates.longitude === 'number'
        ? coordinates.longitude
        : '—';
    return `${latitude}, ${longitude}`;
  }
  if (MONEY_KEYS.has(key) && (typeof value === 'number' || typeof value === 'string')) {
    return formatMoney(value);
  }
  if (key === 'availability_window' && typeof value === 'object' && value !== null) {
    const window = value as { available_from?: unknown; available_to?: unknown };
    if (
      typeof window.available_from === 'string' &&
      typeof window.available_to === 'string'
    ) {
      return availabilityWindowLabel(window.available_from, window.available_to, t);
    }
  }
  if ((key === 'cook_time_seconds' || key === 'cook_time') && typeof value === 'number') {
    return t('review.minutes', '{{n}} мин.').replace(
      '{{n}}',
      String(Math.round(value / 60)),
    );
  }
  if (key === 'promo_type' && typeof value === 'string') {
    const normalized = value.toUpperCase();
    if (normalized.includes('BUY')) return t('review.buyXGetY', 'Купи X — получи Y');
    if (normalized.includes('DISCOUNT')) {
      return t('promoCreate.discountSection', 'Скидка');
    }
  }
  if (key === 'discount_type' && typeof value === 'string') {
    const normalized = value.toUpperCase();
    if (normalized.includes('PERCENT')) {
      return t('promoCreate.discountTypePercent', 'Процентная скидка');
    }
    if (normalized.includes('FIXED')) {
      return t('promoCreate.discountTypeFixed', 'Суммарная скидка');
    }
  }
  if (typeof value === 'boolean') {
    return value ? t('detail.yes', 'Да') : t('detail.no', 'Нет');
  }
  if (key.endsWith('_at') && typeof value === 'string') return formatDateTime(value);
  if (key === 'lifecycle_status' && typeof value === 'string') {
    return lifecycleLabel(value, t);
  }
  if (typeof value === 'string' && value.toUpperCase().startsWith('PRODUCT_STATUS_')) {
    return productStatusLabel(value, t);
  }
  return formatChangedFieldValue(key, value);
}

const ROUND_THE_CLOCK = new Set(['00:00', '0:00']);
const END_OF_DAY = new Set(['24:00', '23:59', '00:00']);

/** `07:00 – 19:00`; the full day reads as "round the clock". */
export function availabilityWindowLabel(from: string, to: string, t: Translate): string {
  const range = `${from} – ${to}`;
  return ROUND_THE_CLOCK.has(from) && END_OF_DAY.has(to)
    ? `${t('review.roundTheClock', 'Круглосуточно')} (${range})`
    : range;
}

/**
 * `PRODUCT_UNAVAILABLE_REASON_*` → words. The "unspecified" default is the
 * absence of a reason, so it reads as empty instead of a technical enum.
 */
export function unavailableReasonLabel(value: string, t: Translate): string {
  const normalized = value
    .trim()
    .toUpperCase()
    .replace('PRODUCT_UNAVAILABLE_REASON_', '');
  switch (normalized) {
    case '':
    case 'UNSPECIFIED':
      return '—';
    case 'DISABLED':
      return t('unavailableReason.disabled', 'Отключено вручную');
    case 'OUT_OF_STOCK':
      return t('unavailableReason.outOfStock', 'Нет в наличии');
    case 'OUT_OF_SCHEDULE':
      return t('unavailableReason.outOfSchedule', 'Вне часов продажи');
    default:
      return value;
  }
}
